<template>
  <Menu.Root id="switch-newsletter" :force-floating="showDemoDetails">
    <Menu.Trigger>
      <button
        type="button"
        class="flex min-w-0 select-none items-center gap-1.5 rounded-md px-2 py-1 text-sm font-semibold text-gray-800 outline-2 outline-blue-500 transition hover:bg-gray-200 focus-visible:outline active:bg-gray-200 data-[state=open]:bg-gray-200 dark:text-white dark:hover:bg-card dark:data-[state=open]:bg-card"
      >
        <NewsletterIcon :newsletter="current" class="-ml-1" />

        <span class="truncate">{{ current.name }}</span>
      </button>
    </Menu.Trigger>

    <Menu.Container :class="showDemoDetails ? 'w-[60rem]' : undefined">
      <Menu.Filter v-if="newsletters.length > 5" v-model="searchQuery" />

      <!-- Dev / demo: many showcase newsletters, so drop the icon and group
           them into labelled columns (locale, creator, personal, etc.). -->
      <div v-if="showDemoDetails" class="columns-3 gap-4">
        <div
          v-for="group in groupedNewsletters"
          :key="group.label"
          class="mb-2 break-inside-avoid"
        >
          <p
            class="px-3 pb-0.5 pt-1 text-xs font-semibold uppercase tracking-wide text-menu-muted-foreground"
          >
            {{ group.label }}
          </p>
          <DemoNewsletterItem
            v-for="item in group.items"
            :key="item.id"
            :newsletter="item"
            :current="item.id === current.id"
            @select="accountStore.switchNewsletter(item.id)"
          />
        </div>
      </div>

      <Draggable v-else v-model="newsletters">
        <Menu.CheckboxItem
          v-for="(item, index) in filteredNewsletters"
          :key="item.id"
          :model-value="item.id === current.id"
          :index="trimmedQuery ? undefined : index"
          :label="item.name || item.username"
          :class="{
            'opacity-50': item.status === 'dormant',
          }"
          @update:model-value="accountStore.switchNewsletter(item.id)"
        >
          <template #icon>
            <NewsletterIcon :newsletter="item" />
          </template>
        </Menu.CheckboxItem>
      </Draggable>

      <Menu.RouteItem
        v-if="!showDemoDetails"
        label="Add Newsletter"
        :to="{ name: '/settings/danger-zone/add-newsletter' }"
      >
        <template #icon>
          <PlusIcon
            class="size-5 rounded-md bg-menu-muted p-1 text-menu-foreground"
          />
        </template>
      </Menu.RouteItem>
    </Menu.Container>
  </Menu.Root>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import Menu from "@/design_system/Menu";
import PlusIcon from "@/icons/heroicons/plus-micro.svg";
import { IS_ADMIN, IS_DEMO } from "@/lib/environment";
import Draggable from "@/lib/pragmatic-dnd/Draggable.vue";
import { useAccountStore, useNewsletter } from "@/store/stores/account";

import DemoNewsletterItem from "./DemoNewsletterItem.vue";
import { useSortableNewsletters } from "./lib";
import NewsletterIcon from "./NewsletterIcon.vue";

const accountStore = useAccountStore();

// In dev (admin host) or the demo deployment there are many showcase
// newsletters, so widen the switcher and surface each one's description +
// username to make them distinguishable.
const showDemoDetails = IS_ADMIN || IS_DEMO;

const current = useNewsletter();

const newsletters = useSortableNewsletters();
const searchQuery = ref("");

const trimmedQuery = computed(() => {
  return searchQuery.value.trim().toLowerCase();
});

const filteredNewsletters = computed(() => {
  const q = trimmedQuery.value;
  if (!q) {
    return newsletters.value;
  }

  return newsletters.value.filter((newsletter) => {
    const name = (newsletter.name || newsletter.username || "").toLowerCase();
    return name.includes(q);
  });
});

// Buckets for the dev/demo switcher. Order here is the column-flow order.
const CATEGORY_ORDER = [
  "Locale",
  "Creator",
  "Personal",
  "Entrepreneur",
] as const;

type Category = (typeof CATEGORY_ORDER)[number];
type NewsletterItem = (typeof filteredNewsletters)["value"][number];

// The demo set is a fixed showcase, and its archetype (creator / personal /
// entrepreneur) isn't derivable from data signals — so bucket the English
// newsletters explicitly by username. Each archetype spans three maturity
// levels (nascent / growing / established). Locale newsletters are detected
// from their locale instead. Anything unmapped (e.g. edge cases) falls into
// Personal so it stays switchable.
const CATEGORY_BY_USERNAME: Record<string, Category> = {
  "beacon-changelog": "Entrepreneur",
  "cat-photographer": "Creator",
  "chicago-forager": "Personal",
  "church-groups": "Personal",
  "css-weekly": "Entrepreneur",
  "long-way-round": "Personal",
  "modular-notes": "Creator",
  "moogle-reader": "Entrepreneur",
  sarahchen: "Creator",
};

const categoryFor = (nl: NewsletterItem): Category => {
  return CATEGORY_BY_USERNAME[nl.username];
};

const groupedNewsletters = computed(() =>
  CATEGORY_ORDER.map((label) => {
    let items = filteredNewsletters.value.filter(
      (nl) => categoryFor(nl) === label
    );
    // Locale newsletters carry verbose "Demonstrates X locale…" descriptions;
    // in the switcher the locale code alone is enough.
    if (label === "Locale") {
      items = items.map((nl) => ({ ...nl, description: nl.locale }));
    }
    return { label, items };
  }).filter((group) => group.items.length > 0)
);
</script>
